import React, { useEffect, useState } from 'react';
import { withErrorBoundary, toast, useProduct } from '@nile/components';
import { useMutation } from '@apollo/client';
import { AccountClient } from '@nile/graphql';
import {
  SHARE_REPORT,
  GET_REPORT_SCHEDULE,
  UPDATE_REPORT_SCHEDULE
} from '@nile/reports/src/modules/reports/graphql';
import { NOTIFICATION_MESSAGE } from '@nile/reports/src/modules/reports/constants';
import { Modal } from '@superops/ui-design-system';
import { ShareContextProvider } from './ShareContext';
import NewShareForm from './NewShareForm';
import { removePropertiesForItProduct } from '@nile/itsm';

function NewReportShare(props) {
  const { data, sheetOpen, toggleSheetOpen, isScheduled } = props;
  const currentProduct = useProduct();

  const [shareLoading, setShareLoading] = useState(false);

  const [dataToShare, setDataToShare] = useState(data);

  const [schedules, setSchedules] = useState([]);

  const [scheduleLoading, setScheduleLoading] = useState(true);

  const [updateReportSchedule] = useMutation(UPDATE_REPORT_SCHEDULE, {
    client: AccountClient,
    onCompleted({ updateReportSchedules }) {
      setSchedules(updateReportSchedules?.schedules);
      toast(
        'success',
        NOTIFICATION_MESSAGE?.SCHEDULE?.SUCCESS?.[
          dataToShare?.reportType || 'METRIC'
        ]
      );
    },
    onError() {
      toast(
        'error',
        NOTIFICATION_MESSAGE?.SCHEDULE?.ERROR?.[
          dataToShare?.reportType || 'METRIC'
        ]
      );
    }
  });

  const [shareReportWithInterceptor] = useMutation(SHARE_REPORT, {
    client: AccountClient,
    onCompleted() {
      toast(
        'success',
        NOTIFICATION_MESSAGE?.SHARE?.SUCCESS?.[
          dataToShare?.reportType || 'METRIC'
        ]
      );
    },
    onError() {
      toast(
        'error',
        NOTIFICATION_MESSAGE?.SHARE?.ERROR?.[
          dataToShare?.reportType || 'METRIC'
        ]
      );
    }
  });

  async function shareReport(variables) {
    try {
      const updatedPayload = removePropertiesForItProduct(
        variables,
        'shareReport',
        currentProduct
      );
      return await shareReportWithInterceptor(updatedPayload);
    } catch (ex) {
      return ex;
    }
  }

  const getReportSchedule = async () => {
    await AccountClient.query({
      query: GET_REPORT_SCHEDULE,
      variables: {
        input: {
          reportId: data?.reportId
        }
      },
      fetchPolicy: 'network-only'
    })
      .then((res) => {
        setSchedules(res?.data?.getReportSchedules?.schedules);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setScheduleLoading(false);
      });
  };

  const providerValue = {
    updateContext: setDataToShare,
    primaryAction: isScheduled ? updateReportSchedule : shareReport
  };

  useEffect(() => {
    getReportSchedule();
    return () => {
      console.log('Component unmounted');
    };
  }, []);

  return (
    <>
      <Modal
        open
        onClose={() => {
          toggleSheetOpen({ value: false, content: null });
        }}
        showCloseButton={false}
        large
      >
        <ShareContextProvider value={providerValue}>
          {sheetOpen.value && (
            <NewShareForm
              toggleSheetActive={() => {
                toggleSheetOpen({ value: false, content: null });
              }}
              isScheduled={isScheduled}
              shareLoading={shareLoading}
              setShareLoading={setShareLoading}
              shareData={dataToShare}
              schedules={schedules}
              scheduleLoading={scheduleLoading}
            />
          )}
        </ShareContextProvider>
      </Modal>
    </>
  );
}

export default withErrorBoundary(NewReportShare);
